import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import ProcessingFeeInfoModal from "views/Modals/EarningsPayout/ProcessingFeeInfoModal";
import { getTransactionHistory } from "../../../../shared/actions/transactionAction";
import {
    PAYMENT_METHOD_MAP,
    getCryptoPayerDisplayName,
    getCryptoCurrencyType,
    formatPaymentMethod,
    CRYPTO_CURRENCY_ICON,
} from "../../../../shared/constants/EarningsPayout";
import { formatWalletAddress } from "../../../../shared/utils/transactionUtil";
import { getRelativeTime, copyTextToClipboard, formatDateTimeZone } from "../../../../shared/utils/function";
import { Pagination } from "../../../../shared/utils/paginationUtil";
import "./earning.css";

// Constants
const INITIAL_PAGE = 1;
const RECORDS_PER_PAGE = 10;
const TILES_PER_PAGE = 5;
const COPIED_WALLET_RESET_MS = 2000;

// Status mapping constants
const STATUS_DISPLAY_MAP = {
    PENDING: "Initiated",
    EXPIRED: "Expired",
    PROCESSING: "Initiated",
    PROCESSED: "Processed",
    COMPLETED: "Processed",
    CANCELLED: "Cancelled",
    SCHEDULED: "Initiated",
    READY_FOR_PICKUP: "Initiated",
    HOLD: "On Hold",
    ERROR: "Error",
    FAILED: "Failed",
};

const STATUS_CLASS_MAP = {
    PROCESSED: "cm_processed",
    COMPLETED: "cm_processed",
    PENDING: "cm_pending",
    PROCESSING: "cm_pending",
    SCHEDULED: "cm_pending",
    READY_FOR_PICKUP: "cm_pending",
    HOLD: "cm_pending",
    CANCELLED: "cm_cancelled",
    EXPIRED: "cm_cancelled",
    ERROR: "cm_failed",
    FAILED: "cm_failed",
};

const STATUS_ICON_MAP = {
    PROCESSED: "fa fa-check",
    COMPLETED: "fa fa-check",
    PENDING: "fa fa-clock",
    PROCESSING: "fa fa-clock",
    SCHEDULED: "fa fa-calendar",
    READY_FOR_PICKUP: "fa fa-check-circle",
    HOLD: "fa fa-pause",
    CANCELLED: "fa fa-times",
    EXPIRED: "fa fa-times-circle",
    ERROR: "fa fa-exclamation-triangle",
    FAILED: "fa fa-exclamation-triangle",
};

// Helper functions
const formatAmount = (amount) => {
    return Number(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatBankInfo = (deliveryType, accountLastDigits, payerName) => {
    const accountNo = typeof accountLastDigits === "string" ? accountLastDigits.slice(-4) : "";
    if (deliveryType === PAYMENT_METHOD_MAP.BANK_DEPOSIT) {
        return `Bank ****${accountNo}`;
    } else if (deliveryType === PAYMENT_METHOD_MAP.CRYPTOCURRENCY) {
        const cryptoCurrencyType = getCryptoCurrencyType(payerName);
        return `${cryptoCurrencyType} ****${accountNo}`;
    }
    return payerName || "Direct Deposit";
};

const getStatusDisplay = (status) => STATUS_DISPLAY_MAP[status] || status;
const getStatusClass = (status) => STATUS_CLASS_MAP[status] || "";
const getStatusIcon = (status) => STATUS_ICON_MAP[status] || "";

const normalizeTransactionStatus = (status) => status?.toUpperCase() || status;

const transformTransaction = (transaction) => {
    const normalizedStatus = normalizeTransactionStatus(transaction.status);

    return {
        ...transaction,
        status: normalizedStatus,
        isOpen: false,
        formattedDate: formatDateTimeZone(transaction.time_of_txn),
        dateString: transaction.time_of_txn,
        formattedTotal: formatAmount(transaction.source_amount),
        formattedFee: formatAmount(transaction.fee || 0),
        formattedNetAmount: formatAmount(transaction.source_amount),
        statusDisplay: getStatusDisplay(normalizedStatus),
        statusClass: getStatusClass(normalizedStatus),
        bankInfo: formatBankInfo(
            transaction.delivery_type,
            transaction.account_last_digits,
            transaction.payer_name
        ),
        token: transaction._id || transaction.token,
    };
};

const PayoutHistoryCard = ({ refreshKey = 0 }) => {
    const [isShow, setIsShow] = useState(true);
    const [loading, setLoading] = useState(false);
    const [payoutHistoryList, setPayoutHistoryList] = useState([]);
    const [processingFeeInfoModal, setProcessingFeeInfoModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [copiedWalletToken, setCopiedWalletToken] = useState(null);
    const copiedWalletTimerRef = useRef(null);

    useEffect(() => {
        const fetchTransactionHistory = async () => {
            const isInitialLoad = refreshKey === 0;
            if (isInitialLoad) {
                setLoading(true);
            }
            try {
                const result = await getTransactionHistory({
                    number_of_records: RECORDS_PER_PAGE,
                    page: INITIAL_PAGE,
                });

                if (result.success && result.data) {
                    const transactions = result.data?.transactions || [];
                    setPayoutHistoryList(transactions.map(transformTransaction));
                    setCurrentPage(1);
                } else {
                    if (result?.error !== "User account method not found.") {
                        toast.error(result.error || "Failed to fetch transaction history");
                    }
                }
            } catch (error) {
                toast.error(error.message || "An unexpected error occurred");
            } finally {
                if (isInitialLoad) {
                    setLoading(false);
                }
            }
        };

        fetchTransactionHistory();
    }, [refreshKey]);

    useEffect(() => {
        return () => {
            if (copiedWalletTimerRef.current) {
                clearTimeout(copiedWalletTimerRef.current);
            }
        };
    }, []);

    const handleChildTabs = (token) => {
        setPayoutHistoryList((prev) =>
            prev.map((item) => (item.token === token ? { ...item, isOpen: !item.isOpen } : item))
        );
    };

    const toggleCard = () => {
        setIsShow((prev) => !prev);
    };

    const totalPages = Math.ceil(payoutHistoryList.length / TILES_PER_PAGE) || 1;
    const visiblePayouts = payoutHistoryList.slice(
        (currentPage - 1) * TILES_PER_PAGE,
        currentPage * TILES_PER_PAGE
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCopyWallet = async (token, fullWalletAddress) => {
        const copied = await copyTextToClipboard(fullWalletAddress);
        if (!copied) return;

        setCopiedWalletToken(token);

        if (copiedWalletTimerRef.current) {
            clearTimeout(copiedWalletTimerRef.current);
        }

        copiedWalletTimerRef.current = setTimeout(() => {
            setCopiedWalletToken(null);
        }, COPIED_WALLET_RESET_MS);
    };


    const renderCryptoWalletRow = (item, cryptoPayerName) => {
        if (item.delivery_type !== PAYMENT_METHOD_MAP.CRYPTOCURRENCY) return null;
        if (!cryptoPayerName || !item.account_last_digits) return null;

        return (
            <li className="cm_sm_list cm_sm_list_gray">
                <div className="d-flex gap-3 align-items-center w-full justify-content-between">
                    <p className="text-white">
                        {cryptoPayerName}
                    </p>
                    <div className="d-flex align-items-center gap-2">
                        <p className="mb-0">
                            <b>{formatWalletAddress(item.account_last_digits, true)}</b>
                        </p>

                        {copiedWalletToken === item.token && (
                            <span
                                className="small"
                                style={{ color: '#198754' }}
                                aria-live="polite"
                            >
                                Copied!
                            </span>
                        )}

                        <span
                            onClick={() => handleCopyWallet(item.token, item.account_last_digits)}
                            style={{ cursor: 'pointer', color: '#6c757d' }}
                            title="Copy full address"
                            aria-label="Copy wallet address"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleCopyWallet(item.token, item.account_last_digits);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faCopy} />
                        </span>
                    </div>
                </div>
            </li>
        );
    };

    return (
        <div className={`for_q_icon g_card ${isShow ? "" : "card_close"}`}>
            <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">
                    <i className="fa-solid fa-money-bill-transfer"></i> Payout History
                </h3>
                <span onClick={toggleCard} className="cm_toggle_card">
                    <FontAwesomeIcon icon={isShow ? faAngleDown : faAngleUp} />
                </span>
            </div>

            {isShow && (
                <>
                    <div className="cm_hr_full"></div>
                    <ul className="payouts_list">
                        {loading ? (
                            <li className="text-center py-3">
                                <p className="text-white">Loading...</p>
                            </li>
                        ) : visiblePayouts.length === 0 ? (
                            <li className="text-center py-3">
                                <p className="text-white">No payout history available</p>
                            </li>
                        ) : (
                            visiblePayouts.map((item) => {
                                const relativeTime = getRelativeTime(item.dateString);
                                const statusIcon = getStatusIcon(item.status);
                                const isCrypto = item.delivery_type === PAYMENT_METHOD_MAP.CRYPTOCURRENCY;
                                const deliveryMethod = isCrypto ? "Cryptocurrency" : "Direct Deposit";

                                const cryptoCurrencyType = isCrypto ? getCryptoCurrencyType(item?.payer_name) : "";
                                const cryptoPayerName = isCrypto ? getCryptoPayerDisplayName(item?.payer_name) : "";

                                return (
                                    <React.Fragment key={item.token}>
                                        <li className={item.isOpen ? "" : "menu_close"}>
                                            <div className="d-flex gap-3 align-items-center">
                                                <p className="cm_payout_date">
                                                    <span className="text-white font_14">
                                                        {item.formattedDate}
                                                    </span>
                                                    {relativeTime && <div>{relativeTime}</div>}
                                                </p>
                                                <div className="cm_h_divder"></div>
                                                <p>
                                                    <b>
                                                        ${item.formattedTotal}
                                                        <span className={item.statusClass}>
                                                            <span
                                                                className={
                                                                    statusIcon
                                                                        ? "status-icon-wrapper"
                                                                        : "status-icon-wrapper-empty"
                                                                }
                                                            >
                                                                {statusIcon && <i className={statusIcon}></i>}
                                                            </span>
                                                            {item.statusDisplay}
                                                        </span>
                                                    </b>
                                                    <div>{formatPaymentMethod(item.delivery_type, item.payer_name)}</div>
                                                </p>
                                            </div>
                                            <div>
                                                <span
                                                    onClick={() => handleChildTabs(item.token)}
                                                    className="cm_toggle_card_s"
                                                >
                                                    <FontAwesomeIcon icon={item.isOpen ? faAngleUp : faAngleDown} />
                                                </span>
                                            </div>
                                        </li>
                                        {item.isOpen && (
                                            <>
                                                <li className="cm_sm_list">
                                                    <div className="d-flex gap-3 align-items-center w-full justify-content-between">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i
                                                                className={`mr-1 ${deliveryMethod === "Cryptocurrency"
                                                                    ? CRYPTO_CURRENCY_ICON[cryptoCurrencyType]
                                                                    : "fa-solid fa-building-columns"
                                                                    }`}
                                                                aria-hidden="true"
                                                            />
                                                            <p className="text-white mb-0">
                                                                {deliveryMethod}
                                                            </p>
                                                        </div>
                                                        <p className="mb-0">
                                                            <b>{item.bankInfo}</b>
                                                        </p>
                                                    </div>
                                                </li>

                                                {renderCryptoWalletRow(item, cryptoPayerName)}

                                                {item.fee > 0 && (
                                                    <li className="cm_sm_list cm_sm_list_gray">
                                                        <div className="d-flex gap-3 align-items-center w-full justify-content-between">
                                                            <p className="text-white">
                                                                Processing Fee
                                                                <span
                                                                    className="question_btn_wrap"
                                                                    onClick={() => setProcessingFeeInfoModal(true)}
                                                                >
                                                                    <i className="fa-solid fa-question"></i>
                                                                </span>
                                                            </p>
                                                            <p><b>${item.formattedFee}</b></p>
                                                        </div>
                                                    </li>
                                                )}

                                                <li className="cm_sm_list cm_sm_list_gray">
                                                    <div className="d-flex gap-3 align-items-center w-full justify-content-between">
                                                        <p className="text-white">Net Amount</p>
                                                        <p><b>${item.formattedNetAmount}</b></p>
                                                    </div>
                                                </li>
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </ul>

                    {payoutHistoryList.length > TILES_PER_PAGE && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            onPreviousPage={() => handlePageChange(currentPage - 1)}
                            onNextPage={() => handlePageChange(currentPage + 1)}
                        />
                    )}

                    <ProcessingFeeInfoModal
                        isOpen={processingFeeInfoModal}
                        toggle={() => setProcessingFeeInfoModal(false)}
                    />
                </>
            )}
        </div>
    );
};

export default PayoutHistoryCard;